import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Autocomplete,
  InputAdornment,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowBack, Add, Delete, Save, Send, CreditCard } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { addDays } from 'date-fns';
import { useCreateInvoice, useNextInvoiceNumber } from '../../hooks/useInvoices';
import { useClients } from '../../hooks/useClients';
import { useServices } from '../../hooks/useServices';
import CurrencyInput from '../../components/common/CurrencyInput';

const lineItemSchema = z.object({
  description: z.string().min(1, 'Description is required'),
  quantity: z.number().min(0.01, 'Quantity must be greater than 0'),
  unitPrice: z.number().min(0, 'Price must be 0 or greater'),
});

const createInvoiceSchema = z.object({
  clientId: z.string().min(1, 'Client is required'),
  issueDate: z.date(),
  dueDate: z.date(),
  lineItems: z.array(lineItemSchema).min(1, 'At least one line item is required'),
  taxRate: z.number().min(0).max(100),
  discountType: z.enum(['fixed', 'percentage']).nullable(),
  discountValue: z.number().min(0),
  notes: z.string().optional(),
  terms: z.string().optional(),
});

type CreateInvoiceFormData = z.infer<typeof createInvoiceSchema>;

interface Service {
  _id: string;
  name: string;
  price: number;
}

export default function CreateInvoicePage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const createInvoice = useCreateInvoice();
  const { data: nextNumber } = useNextInvoiceNumber();
  const { data: clientsData } = useClients({ status: 'active', limit: 100 });
  const { data: servicesData } = useServices({ status: 'active', limit: 100 });

  // Create service options for the description autocomplete
  const serviceOptions = servicesData?.services.map((s) => ({
    label: s.name,
    price: s.price / 100,  // Convert cents to dollars
  })) || [];

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateInvoiceFormData>({
    resolver: zodResolver(createInvoiceSchema),
    defaultValues: {
      clientId: '',
      issueDate: new Date(),
      dueDate: addDays(new Date(), 30),
      lineItems: [{ description: '', quantity: 1, unitPrice: 0 }],
      taxRate: 0,
      discountType: null,
      discountValue: 0,
      notes: '',
      terms: 'Payment due within 30 days of invoice date.',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'lineItems',
  });

  const lineItems = watch('lineItems');
  const clientId = watch('clientId');
  const taxRate = watch('taxRate');
  const discountType = watch('discountType');
  const discountValue = watch('discountValue');

  // Check if form is ready to submit (client selected + at least one line item with description)
  const hasClient = !!clientId;
  const hasValidLineItem = lineItems.some((item) => item.description.trim() !== '');
  const canSubmit = hasClient && hasValidLineItem;

  // Calculate totals
  const subtotal = lineItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice * 100), 0);
  const discountAmount = discountType === 'fixed'
    ? discountValue * 100
    : discountType === 'percentage'
      ? Math.round(subtotal * (discountValue / 100))
      : 0;
  const taxableAmount = subtotal - discountAmount;
  const taxAmount = Math.round(taxableAmount * (taxRate / 100));
  const total = subtotal - discountAmount + taxAmount;

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const handleAddService = (service: Service) => {
    append({
      description: service.name,
      quantity: 1,
      unitPrice: service.price / 100,  // Convert cents to dollars
    });
  };

  const handleApplyCreditCardFee = () => {
    // Check if fee already exists to prevent duplicates
    const feeExists = lineItems.some(item =>
      item.description.toLowerCase().includes('credit card') ||
      item.description.toLowerCase().includes('processing fee')
    );

    if (feeExists) {
      return;
    }

    // Calculate fee based on current subtotal
    // Stripe fee: 2.9% + $0.30
    const feePercentage = 0.029;
    const fixedFee = 0.30;
    const subtotalDollars = subtotal / 100; // Convert cents to dollars
    const fee = (subtotalDollars * feePercentage) + fixedFee;
    const roundedFee = Math.round(fee * 100) / 100; // Round to 2 decimal places

    // Add line item for the fee
    append({
      description: 'Credit Card Processing Fee (2.9% + $0.30)',
      quantity: 1,
      unitPrice: roundedFee,
    });

    // Get current notes value and append fee explanation if not already present
    const currentNotes = watch('notes') || '';
    const feeNote = 'A processing fee has been applied to cover credit card transaction costs.';

    if (!currentNotes.includes('processing fee')) {
      setValue('notes', currentNotes ? `${currentNotes}\n\n${feeNote}` : feeNote);
    }
  };

  const onValidationError = () => {
    // Build a user-friendly error message
    const errorMessages: string[] = [];

    if (errors.clientId) {
      errorMessages.push('Please select a client');
    }
    if (errors.lineItems) {
      if (Array.isArray(errors.lineItems)) {
        errors.lineItems.forEach((itemError, index) => {
          if (itemError?.description) {
            errorMessages.push(`Line item ${index + 1}: Description is required`);
          }
          if (itemError?.quantity) {
            errorMessages.push(`Line item ${index + 1}: Quantity must be greater than 0`);
          }
        });
      } else if (errors.lineItems.message) {
        errorMessages.push(errors.lineItems.message);
      }
    }

    setValidationError(errorMessages.length > 0 ? errorMessages.join('. ') : 'Please fill in all required fields');
  };

  const onSubmit = async (data: CreateInvoiceFormData, status: 'draft' | 'sent') => {
    setError(null);
    setValidationError(null);

    try {
      const invoice = await createInvoice.mutateAsync({
        clientId: data.clientId,
        lineItems: data.lineItems.map((item) => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: Math.round(item.unitPrice * 100),  // Convert to cents
        })),
        issueDate: data.issueDate.toISOString(),
        dueDate: data.dueDate.toISOString(),
        taxRate: data.taxRate / 100,  // Convert percentage to decimal
        discountType: data.discountType,
        discountValue: data.discountType === 'fixed' ? data.discountValue * 100 : data.discountValue,
        notes: data.notes,
        terms: data.terms,
        status,
      });

      navigate(`/invoices/${invoice._id}`);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || 'Failed to create invoice');
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/invoices')}>
            <ArrowBack />
          </IconButton>
          <Box flex={1}>
            <Typography variant="h4" fontWeight={600}>
              New Invoice
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {nextNumber || 'Loading...'}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {validationError && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {validationError}
          </Alert>
        )}

        <form>
          <Grid container spacing={3}>
            {/* Left Column - Invoice Details */}
            <Grid item xs={12} md={8}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Invoice Details
                  </Typography>

                  <Grid container spacing={2}>
                    {/* Client */}
                    <Grid item xs={12}>
                      <Controller
                        name="clientId"
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            options={clientsData?.clients || []}
                            getOptionLabel={(option) =>
                              typeof option === 'string'
                                ? option
                                : `${option.firstName} ${option.lastName}`
                            }
                            value={clientsData?.clients.find((c) => c._id === field.value) || null}
                            onChange={(_, newValue) => {
                              field.onChange(newValue?._id || '');
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Client"
                                required
                                error={!!errors.clientId}
                                helperText={errors.clientId?.message}
                              />
                            )}
                          />
                        )}
                      />
                    </Grid>

                    {/* Dates */}
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="issueDate"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            label="Issue Date"
                            value={field.value}
                            onChange={(newValue) => field.onChange(newValue)}
                            slotProps={{
                              textField: { fullWidth: true, required: true },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="dueDate"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            label="Due Date"
                            value={field.value}
                            onChange={(newValue) => field.onChange(newValue)}
                            slotProps={{
                              textField: { fullWidth: true, required: true },
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Line Items */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Line Items</Typography>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>Add Service</InputLabel>
                      <Select
                        label="Add Service"
                        value=""
                        onChange={(e) => {
                          const service = servicesData?.services.find((s) => s._id === e.target.value);
                          if (service) handleAddService(service);
                        }}
                      >
                        {servicesData?.services.map((service) => (
                          <MenuItem key={service._id} value={service._id}>
                            {service.name} - {formatCurrency(service.price)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: isMobile ? 500 : 'auto' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: isMobile ? 180 : 'auto' }}>Description</TableCell>
                          <TableCell align="center" sx={{ width: { xs: 80, sm: 100 }, minWidth: 80 }}>Qty</TableCell>
                          <TableCell align="right" sx={{ width: { xs: 150, sm: 160 }, minWidth: 150 }}>Price</TableCell>
                          <TableCell align="right" sx={{ width: { xs: 100, sm: 120 }, minWidth: 100 }}>Amount</TableCell>
                          <TableCell sx={{ width: 50 }}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fields.map((field, index) => (
                          <TableRow key={field.id}>
                            <TableCell sx={{ verticalAlign: 'top', pt: 1.5 }}>
                              <Controller
                                name={`lineItems.${index}.description`}
                                control={control}
                                render={({ field: descField }) => (
                                  <Autocomplete
                                    freeSolo
                                    options={serviceOptions}
                                    getOptionLabel={(option) =>
                                      typeof option === 'string' ? option : option.label
                                    }
                                    value={descField.value}
                                    onChange={(_, newValue) => {
                                      if (typeof newValue === 'string') {
                                        descField.onChange(newValue);
                                      } else if (newValue) {
                                        descField.onChange(newValue.label);
                                        // Auto-fill the price when selecting a service
                                        setValue(`lineItems.${index}.unitPrice`, newValue.price);
                                      }
                                    }}
                                    onInputChange={(_, newInputValue, reason) => {
                                      if (reason === 'input') {
                                        descField.onChange(newInputValue);
                                      }
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        size="small"
                                        fullWidth
                                        placeholder="Type or select service"
                                        error={!!errors.lineItems?.[index]?.description}
                                      />
                                    )}
                                    renderOption={(props, option) => (
                                      <li {...props} key={option.label}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                          <span>{option.label}</span>
                                          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                            {formatCurrency(option.price * 100)}
                                          </Typography>
                                        </Box>
                                      </li>
                                    )}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', pt: 1.5, minWidth: 80 }}>
                              <Controller
                                name={`lineItems.${index}.quantity`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    value={field.value === 0 ? '' : String(field.value)}
                                    type="text"
                                    inputMode="decimal"
                                    size="small"
                                    fullWidth
                                    placeholder="0"
                                    sx={{
                                      '& input': {
                                        textAlign: 'center',
                                        padding: '8px 4px',
                                      },
                                    }}
                                    onFocus={(e) => e.target.select()}
                                    onChange={(e) => {
                                      let sanitized = e.target.value.replace(/[^0-9.]/g, '');
                                      sanitized = sanitized.replace(/^0+(?=\d)/, '');
                                      e.target.value = sanitized;
                                      field.onChange(parseFloat(sanitized) || 0);
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', pt: 1.5, minWidth: 150 }}>
                              <Controller
                                name={`lineItems.${index}.unitPrice`}
                                control={control}
                                render={({ field }) => (
                                  <CurrencyInput
                                    value={field.value}
                                    onChange={field.onChange}
                                    size="small"
                                    fullWidth
                                    sx={{ minWidth: 130 }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ verticalAlign: 'top', pt: 2 }}>
                              <Typography sx={{ whiteSpace: 'nowrap' }}>
                                {formatCurrency((lineItems[index]?.quantity ?? 0) * (lineItems[index]?.unitPrice ?? 0) * 100)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => remove(index)}
                                disabled={fields.length === 1}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box display="flex" gap={2} flexWrap="wrap" sx={{ mt: 2 }}>
                    <Button
                      startIcon={<Add />}
                      onClick={() => append({ description: '', quantity: 1, unitPrice: 0 })}
                    >
                      Add Line Item
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CreditCard />}
                      onClick={handleApplyCreditCardFee}
                      disabled={subtotal === 0}
                      sx={{
                        borderColor: 'grey.400',
                        color: 'text.secondary',
                        '&:hover': {
                          borderColor: 'grey.600',
                          backgroundColor: 'grey.50',
                        }
                      }}
                    >
                      Apply Credit Card Fee
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              {/* Notes */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Notes & Terms
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Controller
                        name="notes"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Notes (visible to client)"
                            multiline
                            rows={2}
                            fullWidth
                            placeholder="Thank you for your business!"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="terms"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Payment Terms"
                            multiline
                            rows={2}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Right Column - Summary */}
            <Grid item xs={12} md={4}>
              <Card sx={{ position: 'sticky', top: 24 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Summary
                  </Typography>

                  {/* Tax */}
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Controller
                      name="taxRate"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Tax Rate"
                          type="number"
                          size="small"
                          sx={{ width: 100 }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          inputProps={{ min: 0, max: 100, step: 0.1 }}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      )}
                    />
                  </Box>

                  {/* Discount */}
                  <Box display="flex" gap={1} mb={2}>
                    <Controller
                      name="discountType"
                      control={control}
                      render={({ field }) => (
                        <FormControl size="small" sx={{ width: 120 }}>
                          <InputLabel>Discount</InputLabel>
                          <Select
                            {...field}
                            label="Discount"
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value || null)}
                          >
                            <MenuItem value="">None</MenuItem>
                            <MenuItem value="fixed">Fixed ($)</MenuItem>
                            <MenuItem value="percentage">Percent (%)</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                    {discountType === 'fixed' && (
                      <Controller
                        name="discountValue"
                        control={control}
                        render={({ field }) => (
                          <CurrencyInput
                            value={field.value}
                            onChange={field.onChange}
                            size="small"
                            sx={{ width: 100 }}
                          />
                        )}
                      />
                    )}
                    {discountType === 'percentage' && (
                      <Controller
                        name="discountValue"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            size="small"
                            sx={{ width: 100 }}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                            inputProps={{ min: 0, max: 100, step: 0.1 }}
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          />
                        )}
                      />
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Totals */}
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography color="text.secondary">Subtotal</Typography>
                    <Typography>{formatCurrency(subtotal)}</Typography>
                  </Box>

                  {discountAmount > 0 && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="text.secondary">Discount</Typography>
                      <Typography color="error">-{formatCurrency(discountAmount)}</Typography>
                    </Box>
                  )}

                  {taxAmount > 0 && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="text.secondary">Tax ({taxRate}%)</Typography>
                      <Typography>{formatCurrency(taxAmount)}</Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box display="flex" justifyContent="space-between" mb={3}>
                    <Typography variant="h6">Total</Typography>
                    <Typography variant="h6">{formatCurrency(total)}</Typography>
                  </Box>

                  {/* Action Buttons */}
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Save />}
                    onClick={handleSubmit((data) => onSubmit(data, 'draft'), onValidationError)}
                    disabled={!canSubmit || isSubmitting}
                    sx={{ mb: 1 }}
                  >
                    Save as Draft
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Send />}
                    onClick={handleSubmit((data) => onSubmit(data, 'sent'), onValidationError)}
                    disabled={!canSubmit || isSubmitting}
                  >
                    Save & Send
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>
      </Box>
    </LocalizationProvider>
  );
}
